import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { getBotMapping } from '../utils/botMapping';
import HandCoins from './ui/icons/HandCoins';

interface OrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedBot: string;
  user: any;
  onOrderCreated?: (orderData: any) => void;
}

export const OrderModal: React.FC<OrderModalProps> = ({
  isOpen,
  onClose,
  selectedBot,
  user,
  onOrderCreated
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language === 'ENG' ? 'ENG' : 'RUS';
  
  // Состояния
  const [step, setStep] = useState<'select' | 'input'>('select');
  const [orderType, setOrderType] = useState<'buy' | 'sell' | null>(null);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [userBalance, setUserBalance] = useState<any>(null);
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);
  
  // Получаем маппинг бота
  const botMapping = getBotMapping(selectedBot);
  
  // Сброс состояния при открытии
  useEffect(() => {
    if (isOpen) {
      setStep('select');
      setOrderType(null);
      setAmount('');
      setError(null);
      setValidationError(null);
      fetchUserBalance();
      fetchCurrentPrice();
    }
  }, [isOpen, selectedBot]);
  
  // Загрузка баланса пользователя
  const fetchUserBalance = async () => {
    if (!user || !selectedBot) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/trading_bot/get_balance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: parseInt(user.user_id),
          bot_name: selectedBot
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setUserBalance(result.balance_data);
        }
      }
    } catch (err) {
      console.error('Error fetching balance:', err);
    }
  };
  
  // Загрузка текущей цены
  const fetchCurrentPrice = async () => {
    if (!selectedBot) return;
    
    try {
      const botMapping = getBotMapping(selectedBot);
      const TRADING_BOT_URL = process.env.REACT_APP_TRADING_BOT_URL as string;
      const response = await fetch(`${TRADING_BOT_URL}/api/bots/${botMapping.botId}/price?pair=${botMapping.tradingPair}`, {
        method: 'GET',
        headers: {
          'X-API-Key': process.env.REACT_APP_TRADING_BOT_API_KEY || ''
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.price) {
          setCurrentPrice(result.price);
        }
      }
    } catch (err) {
      console.error('Error fetching price:', err);
    }
  };
  
  const handleOrderTypeSelect = (type: 'buy' | 'sell') => {
    setOrderType(type);
    setStep('input');
    setError(null);
  };
  
  const handleBack = () => {
    setStep('select');
    setOrderType(null);
    setAmount('');
    setError(null);
    setValidationError(null);
  };

  // Обработка ввода суммы с валидацией
  const handleAmountChange = (value: string) => {
    // Обработка ввода: замена запятой на точку и фильтрация символов
    let processedValue = value;
    
    // Заменяем запятую на точку
    processedValue = processedValue.replace(',', '.');
    
    // Оставляем только цифры и точку
    processedValue = processedValue.replace(/[^0-9.]/g, '');
    
    // Убираем лишние точки (оставляем только первую)
    const parts = processedValue.split('.');
    if (parts.length > 2) {
      processedValue = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Ограничиваем количество знаков после запятой до 6
    if (parts.length === 2 && parts[1].length > 6) {
      processedValue = parts[0] + '.' + parts[1].substring(0, 6);
    }

    setAmount(processedValue);
    setError(null);
    setValidationError(null);

    // Валидация в реальном времени
    if (processedValue && orderType) {
      const numValue = parseFloat(processedValue);
      if (!isNaN(numValue)) {
        const validationResult = validateAmount(numValue);
        if (validationResult) {
          setValidationError(validationResult);
        }
      }
    }
  };

  // Валидация суммы
  const validateAmount = (value: number): string | null => {
    if (orderType === 'buy') {
      // Для покупки: минимальная сумма 1 USDT/USDC
      if (value < 1) {
        return currentLanguage === 'ENG' 
          ? `Minimum amount: 1 ${botMapping.quoteCurrency}`
          : `Минимальная сумма: 1 ${botMapping.quoteCurrency}`;
      }
    } else if (orderType === 'sell') {
      // Для продажи: проверяем, что получаемая сумма будет не менее 1 USDT/USDC
      if (currentPrice && value * currentPrice < 1) {
        return currentLanguage === 'ENG' 
          ? `Minimum sell value: 1 ${botMapping.quoteCurrency} (${(1 / currentPrice).toFixed(6)} ${botMapping.baseCurrency})`
          : `Минимальная сумма продажи: 1 ${botMapping.quoteCurrency} (${(1 / currentPrice).toFixed(6)} ${botMapping.baseCurrency})`;
      }
    }
    return null;
  };
  
  // Проверка достаточности баланса
  const checkBalance = (): { sufficient: boolean; errorMessage: string | null } => {
    if (!userBalance || !amount || !orderType) {
      return { sufficient: false, errorMessage: null };
    }
    
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      return { 
        sufficient: false, 
        errorMessage: currentLanguage === 'ENG' ? 'Invalid amount' : 'Неверная сумма' 
      };
    }
    
    const balance = userBalance.balance?.free || {};
    
    if (orderType === 'buy') {
      // Для покупки проверяем баланс quote валюты (USDT/USDC)
      const quoteBalance = balance[botMapping.quoteCurrency] || 0;
      const requiredAmount = numAmount; // Сумма в quote валюте
      
      if (quoteBalance < requiredAmount) {
        return { 
          sufficient: false, 
          errorMessage: currentLanguage === 'ENG' 
            ? `Insufficient ${botMapping.quoteCurrency} balance. Available: ${quoteBalance.toFixed(6)} ${botMapping.quoteCurrency}`
            : `Недостаточно ${botMapping.quoteCurrency}. Доступно: ${quoteBalance.toFixed(6)} ${botMapping.quoteCurrency}`
        };
      }
    } else {
      // Для продажи проверяем баланс base валюты (KAS/BTC/ETH/XRP)
      const baseBalance = balance[botMapping.baseCurrency] || 0;
      const requiredAmount = numAmount; // Количество в base валюте
      
      if (baseBalance < requiredAmount) {
        return { 
          sufficient: false, 
          errorMessage: currentLanguage === 'ENG' 
            ? `Insufficient ${botMapping.baseCurrency} balance. Available: ${baseBalance.toFixed(6)} ${botMapping.baseCurrency}`
            : `Недостаточно ${botMapping.baseCurrency}. Доступно: ${baseBalance.toFixed(6)} ${botMapping.baseCurrency}`
        };
      }
    }
    
    return { sufficient: true, errorMessage: null };
  };
  
  const handleCreateOrder = async () => {
    if (!amount || !orderType) return;
    
    // Проверяем валидацию
    const numValue = parseFloat(amount);
    if (isNaN(numValue)) {
      setError(currentLanguage === 'ENG' ? 'Invalid amount' : 'Неверная сумма');
      return;
    }
    
    const validationResult = validateAmount(numValue);
    if (validationResult) {
      setValidationError(validationResult);
      return;
    }
    
    // Проверяем баланс
    const balanceCheck = checkBalance();
    if (!balanceCheck.sufficient) {
      setError(balanceCheck.errorMessage);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Создаем реальный ордер через торговый бот
      const botMapping = getBotMapping(selectedBot);
      const TRADING_BOT_URL = process.env.REACT_APP_TRADING_BOT_URL as string;
      
      // Определяем параметры ордера
      let orderAmount, orderPrice, orderTotal;
      
      if (orderType === 'buy') {
        // Для покупки: amount - это сумма в USDT, которую нужно потратить
        orderAmount = parseFloat(amount); // Сумма в USDT
        orderPrice = currentPrice || 0;
        orderTotal = orderAmount; // Потраченная сумма USDT
      } else {
        // Для продажи: amount - это количество криптовалюты для продажи
        orderAmount = parseFloat(amount); // Количество криптовалюты
        orderPrice = currentPrice || 0;
        orderTotal = orderAmount * orderPrice; // Полученная сумма USDT
      }
      
      // Создаем ордер через SPA бэкенд
      const response = await fetch(`${process.env.REACT_APP_API_URL}/trading_bot/create_order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: user.user_id,
          bot_name: selectedBot,
          order_type: orderType,
          amount: orderAmount,
          pair: botMapping.tradingPair
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          // Для покупки: amount должен быть количеством купленных KAS (из result.amount)
          // Для продажи: amount должен быть количеством проданных KAS (из result.amount или orderAmount)
          const actualAmount = result.amount || (orderType === 'sell' ? orderAmount : 0);
          const actualTotal = result.total || (orderType === 'buy' ? orderAmount : (orderAmount * (currentPrice || 0)));
          
          const orderData = {
            success: true,
            type: orderType,
            amount: actualAmount,
            base_currency: botMapping.baseCurrency,
            quote_currency: botMapping.quoteCurrency,
            template_params: {
              order_type: orderType === 'buy' ? 'Покупка' : 'Продажа',
              amount: actualAmount.toFixed(6),
              base_currency: botMapping.baseCurrency,
              quote_currency: botMapping.quoteCurrency,
              price: (result.price || currentPrice || 0).toFixed(6),
              total: actualTotal.toFixed(6)
            }
          };
          
          onOrderCreated?.(orderData);
          onClose();
        } else {
          setError(result.message || (currentLanguage === 'ENG' ? 'Order creation failed' : 'Ошибка создания ордера'));
        }
      } else {
        setError(currentLanguage === 'ENG' ? 'Failed to create order' : 'Не удалось создать ордер');
      }
    } catch (err) {
      setError(currentLanguage === 'ENG' ? 'Order creation error' : 'Ошибка создания ордера');
    } finally {
      setLoading(false);
    }
  };
  
  const handleClose = () => {
    setStep('select');
    setOrderType(null);
    setAmount('');
    setError(null);
    setValidationError(null);
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-header border border-border rounded-lg shadow-lg max-w-md w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="pl-2 pr-1 py-2 border-b border-border">
          <div className="grid grid-cols-7 gap-1">
            <div></div>
            <div className="col-span-5 flex items-center justify-center">
              <h2 className="text-lg font-semibold text-white">
                {t('one_time_order')}
              </h2>
            </div>
            <div className="flex justify-center">
              <button
                onClick={handleClose}
                className="w-8 h-8 text-sm rounded-md transition-colors border border-border text-textSecondary focus:outline-none focus:ring-0 flex items-center justify-center"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4">
          {step === 'select' && (
            <div className="space-y-3">
              <div className="text-sm text-white mb-4">
                {t('select_order_type')}
              </div>
              
              <button
                onClick={() => handleOrderTypeSelect('buy')}
                className="w-full p-3 rounded-lg border border-border bg-header text-white transition-colors focus:outline-none focus:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="9" cy="21" r="1"/>
                    <circle cx="20" cy="21" r="1"/>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                  </svg>
                  <span>{t('buy')}</span>
                </div>
              </button>
              
              <button
                onClick={() => handleOrderTypeSelect('sell')}
                className="w-full p-3 rounded-lg border border-border bg-header text-white transition-colors focus:outline-none focus:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                <div className="flex items-center justify-center gap-2">
                  <HandCoins className="w-5 h-5" />
                  <span>{t('sell')}</span>
                </div>
              </button>
            </div>
          )}
          
          {step === 'input' && orderType && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                               <button
                 onClick={handleBack}
                 className="text-textSecondary transition-colors border border-border rounded px-2 py-1 focus:outline-none focus:ring-0"
                 style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
               >
                 &lt; {t('back')}
               </button>
                <div></div>
                <div></div>
              </div>
              
              <div className="text-sm text-white mb-2">
                {orderType === 'buy' 
                  ? `${t('amount_to_buy')} ${botMapping.baseCurrency}`
                  : `${t('amount_to_sell')} ${botMapping.baseCurrency}`
                }
              </div>
              
              <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                placeholder={orderType === 'buy' 
                  ? `0.00 ${botMapping.quoteCurrency}`
                  : `0.00 ${botMapping.baseCurrency}`
                }
                className={`w-full p-3 rounded-lg border bg-background text-white focus:outline-none focus:ring-0 input-neon-focus ${
                  validationError ? 'border-red-500' : 'border-border'
                }`}
                autoFocus
              />
              
              {validationError && (
                <div className="text-red-400 text-xs mt-1">
                  {validationError}
                </div>
              )}
              
              {error && (
                <div className="text-red-400 text-sm">
                  {error}
                </div>
              )}
              
                                                                          <button
                 onClick={handleCreateOrder}
                 disabled={loading || !amount || !!validationError}
                 className="w-full p-3 rounded-lg border border-border bg-header text-white transition-colors focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                 style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
               >
                 {loading ? t('creating_order') : (orderType === 'buy' ? t('buy') : t('sell'))}
               </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 